<template>
  <div
    class="sticky-note"
    :class="{ highlighted: note.highlighted, dragging: isDragging }"
    :style="{ left: `${note.x * 100}%`, top: `${note.y * 100}%`, zIndex: note.z, backgroundColor: note.color }"
    :data-note-id="note.id"
    data-testid="StickyNote"
  >
    <div
      class="sticky-note-header"
      :style="{ backgroundColor: headerColor }"
      data-testid="StickyNote-drag"
      @pointerdown="startDrag"
    >
      <button
        type="button"
        class="sticky-note-star"
        :aria-label="note.highlighted ? 'Noņemt izcēlumu' : 'Izcelt līmlapu'"
        data-testid="StickyNote-highlight"
        @click="stickyBoardStore.toggleNoteHighlight(note)"
      >
        <v-icon size="12">{{ note.highlighted ? "fa-solid fa-star" : "fa-light fa-star" }}</v-icon>
      </button>
      <button
        v-for="color in colors"
        :key="color"
        type="button"
        class="sticky-note-color"
        :class="{ selected: color === note.color }"
        :style="{ backgroundColor: color }"
        :aria-label="`Krāsa ${color}`"
        data-testid="StickyNote-color"
        @click="stickyBoardStore.setNoteColor(note, color)"
      />
      <button
        type="button"
        class="sticky-note-delete"
        aria-label="Dzēst līmlapu"
        data-testid="StickyNote-delete"
        @click="stickyBoardStore.deleteNote(note)"
      >
        <v-icon size="9">fa-solid fa-xmark</v-icon>
      </button>
    </div>
    <div v-if="showName && note.student_name" class="sticky-note-name" data-testid="StickyNote-name">{{ note.student_name }}</div>
    <textarea
      ref="text"
      class="sticky-note-text"
      :value="note.text"
      placeholder="Raksti šeit..."
      rows="3"
      data-testid="StickyNote-text"
      @input="onInput"
    />
  </div>
</template>

<script lang="ts">
import clamp from "lodash/clamp"
import type { PropType } from "vue"

import { STICKY_COLORS, mixColor, useStickyBoardStore } from "~/stores/stickyBoardStore"
import type { StickyNote } from "~/types/stickyBoard"

export default {
  props: {
    note: {
      type: Object as PropType<StickyNote>,
      required: true,
    },
    showName: {
      type: Boolean,
      required: true,
    },
  },

  setup() {
    const stickyBoardStore = useStickyBoardStore()
    return { stickyBoardStore, colors: STICKY_COLORS }
  },

  data() {
    return {
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
    }
  },

  computed: {
    headerColor(): string {
      return mixColor(this.note.color, 0, 0.92)
    },
  },

  watch: {
    "note.highlighted"() {
      this.$nextTick(() => this.resizeText())
    },
  },

  mounted() {
    this.resizeText()
  },

  unmounted() {
    this.stopDrag()
  },

  methods: {
    onInput(event: Event) {
      this.stickyBoardStore.setNoteText(this.note, (event.target as HTMLTextAreaElement).value)
      this.resizeText()
    },

    resizeText() {
      const textarea = this.$refs.text as HTMLTextAreaElement
      textarea.style.height = "auto"
      textarea.style.height = `${textarea.scrollHeight}px`
    },

    startDrag(event: PointerEvent) {
      this.stickyBoardStore.raiseNote(this.note)
      if ((event.target as HTMLElement).closest("button")) {
        return
      }

      event.preventDefault()
      const rect = (this.$el as HTMLElement).getBoundingClientRect()
      this.isDragging = true
      this.dragOffset = { x: event.clientX - rect.left, y: event.clientY - rect.top }
      window.addEventListener("pointermove", this.onDrag)
      window.addEventListener("pointerup", this.stopDrag)
    },

    onDrag(event: PointerEvent) {
      const element = this.$el as HTMLElement
      const board = (element.offsetParent as HTMLElement).getBoundingClientRect()
      const left = clamp(event.clientX - this.dragOffset.x - board.left, 0, board.width - element.offsetWidth)
      const top = clamp(event.clientY - this.dragOffset.y - board.top, 0, board.height - element.offsetHeight)
      this.stickyBoardStore.moveNote(this.note, left / board.width, top / board.height)
    },

    stopDrag() {
      window.removeEventListener("pointermove", this.onDrag)
      window.removeEventListener("pointerup", this.stopDrag)
      if (this.isDragging) {
        this.isDragging = false
        this.stickyBoardStore.saveNote(this.note)
      }
    },
  },
}
</script>

<style lang="sass" scoped>
.sticky-note
  position: absolute
  width: 180px
  border-radius: 6px
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12)
  overflow: hidden
  border: 2px solid transparent

  &.highlighted
    width: 240px
    border-color: #4A4A3A
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2)

    .sticky-note-text
      font-size: 17px
      min-height: 110px

  &.dragging
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.25)

.sticky-note-header
  display: flex
  align-items: center
  gap: 4px
  padding: 4px 6px
  cursor: grab
  touch-action: none

  .dragging &
    cursor: grabbing

.sticky-note-star
  display: flex
  align-items: center
  justify-content: center
  width: 20px
  height: 20px
  border: none
  border-radius: 4px
  background: transparent
  color: #3A3A2A
  cursor: pointer

  .highlighted &
    background-color: rgba(0, 0, 0, 0.12)

.sticky-note-color
  width: 12px
  height: 12px
  padding: 0
  border-radius: 50%
  border: 1px solid rgba(0, 0, 0, 0.2)
  cursor: pointer

  &.selected
    border: 2px solid #4B5563

.sticky-note-delete
  display: flex
  align-items: center
  justify-content: center
  width: 16px
  height: 16px
  margin-left: auto
  padding: 0
  border: none
  border-radius: 50%
  background-color: rgba(0, 0, 0, 0.15)
  color: #3A3A2A
  cursor: pointer

  &:hover
    background-color: rgba(0, 0, 0, 0.25)

.sticky-note-name
  padding: 4px 10px 0
  font-size: 11px
  color: rgba(0, 0, 0, 0.45)
  text-align: right

.sticky-note-text
  display: block
  width: 100%
  min-height: 90px
  padding: 8px 10px 12px
  border: none
  outline: none
  resize: none
  overflow: hidden
  background: transparent
  font-size: 15px
  line-height: 1.4
  color: #1F2937
  text-align: left
</style>
